import { FormEvent, useEffect, useRef, useState } from 'react';
import type { Message } from '../types';
import { supabase } from '../lib/supabase';
import { formatDateTime, participantLabel } from '../lib/format';
import { useAuth } from '../contexts/AuthContext';

export function ChatBox({ auctionId, messages, locked }: { auctionId: string; messages: Message[]; locked: boolean }) {
  const { user } = useAuth();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
  }, [messages]);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const clean = body.trim();
    if (!clean || !user) return;

    setBusy(true);
    setError(null);
    try {
      const { error: insertError } = await supabase.from('messages').insert({
        auction_id: auctionId,
        user_id: user.id,
        body: clean
      });
      if (insertError) throw insertError;
      setBody('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Messaggio non inviato');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel chat-panel">
      <div className="panel-header compact">
        <h2>Chat asta</h2>
        <span className="muted">{messages.length} messaggi</span>
      </div>

      <div className="messages-list" ref={listRef}>
        {messages.map((message) => {
          const mine = message.user_id === user?.id;
          return (
            <article key={message.id} className={mine ? 'message mine' : 'message'}>
              <div className="message-author">{participantLabel(message.profiles)}</div>
              <p>{message.body}</p>
              <time>{formatDateTime(message.created_at)}</time>
            </article>
          );
        })}
        {messages.length === 0 && <p className="muted centered">Ancora nessun messaggio.</p>}
      </div>

      <form className="chat-form" onSubmit={send}>
        <input
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder={locked ? 'Chat bloccata' : 'Scrivi un messaggio…'}
          disabled={busy || locked}
          maxLength={2000}
        />
        <button className="primary-button" disabled={busy || locked || !body.trim()}>Invia</button>
      </form>
      {error && <p className="error-text">{error}</p>}
    </section>
  );
}
